<template>
  <section
    class="flex-grow bg-campaign-surface-alt px-[16px] pt-[28px] pb-[max(32px,calc(env(safe-area-inset-bottom)+16px))] md:pb-[56px]"
    :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''"
    data-test="bf-dreams"
  >
    <!-- Comments stay inside the root: one before it makes a fragment in dev builds,
         leaving `$el` an empty anchor. -->
    <div class="w-full md:max-w-2xl md:mx-auto flex flex-col gap-[12px]">
      <DsHeading
        tag="h2"
        class="font-pinyon-script font-normal text-[34px] leading-[1.1] text-campaign-primary text-center"
      >
        My Dreams &amp; Prayers
      </DsHeading>

      <!-- v-show, not v-if, so the field is marked once in mounted(), as on the form. -->
      <DsTextarea
        v-show="showEditor"
        v-model="text"
        name="dreams_prayers"
        aria-label="My Dreams & Prayers"
        :rows="5"
        placeholder="Type here…"
        class="[--hogc-textarea-radius:12px] [&_textarea]:min-h-[134px]"
        data-test="bf-dreams-field"
      />
      <!-- Saved: not drawn yet; built from the plan until design confirms it. -->
      <DsCard v-show="!showEditor" class="min-h-[160px]" data-test="bf-dreams-saved">
        <DsBody class="text-text-body-md text-campaign-ink whitespace-pre-line break-words">
          {{ saved }}
        </DsBody>
      </DsCard>

      <div class="flex justify-end">
        <DsButton
          variant="campaign-outline"
          size="36"
          :class="['-mt-[4px] [--hogc-button-padding:0_18px]', buttonClass]"
          :disabled="showEditor && (saving || (!text.trim() && !saved))"
          :data-test="showEditor ? 'bf-dreams-save' : 'bf-dreams-edit'"
          @click="showEditor ? save() : edit()"
        >
          {{ showEditor ? 'Save' : 'Edit' }}
        </DsButton>
      </div>

      <!-- Back to MyGiving on D and V, inside the block as the frames draw it. -->
      <div v-if="$slots.default" class="mt-[28px]">
        <slot />
      </div>
    </div>
  </section>
</template>

<script>
import {
  Body as DsBody,
  Button as DsButton,
  Card as DsCard,
  Heading as DsHeading,
  Textarea as DsTextarea,
} from '@team-hogc/hogc-one-design-system';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';

// "My Dreams & Prayers" (WP7) at the foot of D and V: a field and Save, or the
// saved text and Edit. All three read the store's one copy.
export default {
  name: 'DreamsPrayers',

  mixins: [keyboardFieldIntoView],

  components: { DsBody, DsButton, DsCard, DsHeading, DsTextarea },

  props: {
    // Extra classes for Save / Edit: My BF Fulfilment draws it brown, not navy.
    buttonClass: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      text: '',
      editing: false,
      saving: false,
    };
  },

  computed: {
    saved() {
      return this.$store.state.buildingFundPledge.dreams || '';
    },
    memberId() {
      return this.$store.state.profile?.id;
    },
    showEditor() {
      return this.editing || !this.saved;
    },
  },

  watch: {
    // The profile can arrive after the page (a cold deep link), and the text is
    // keyed by member, so load once there is one.
    memberId: {
      immediate: true,
      handler(id) {
        if (id) this.$store.dispatch('buildingFundPledge/loadDreams');
      },
    },
  },

  // Opts the field out of main.scss's blanket text-input rule (55px, white), as the
  // pledge form does; the DS puts extra attributes on its wrapper, not the field.
  mounted() {
    this.$el.querySelector('textarea')?.setAttribute('data-compact-input', '');
    this.startKeyboardOverlapTracking();
  },

  methods: {
    edit() {
      this.text = this.saved;
      this.editing = true;
    },

    async save() {
      this.saving = true;
      const saved = await this.$store.dispatch('buildingFundPledge/saveDreams', this.text);
      this.saving = false;
      if (saved) this.editing = false;
    },
  },
};
</script>
